import { useQuery } from '@tanstack/react-query'
import JSZip from 'jszip'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { MonthInput } from '../../../components/ui/month-input'
import { config } from '../../../lib/config'
import { supabase } from '../../../lib/supabase'
import { BillTemplate } from './components/BillTemplate'
import type { BillData } from './interfaces/exportBill'
import {
  fetchAllBillsByMonth,
  fetchBillByRoomAndMonth,
  fetchSnapshotBillByRoomAndMonth,
  fetchSnapshotBillsByMonth,
} from './services/exportBillService'

// A4 at 96 DPI
const A4_W = 794
const A4_H = 1123

const EXPORT_BILL_URL = `${config.supabase.url}/functions/v1/export-bill`
const EXPORT_BILL_JPEG_URL = `${config.supabase.url}/functions/v1/export-bill-jpeg`

function createDownloadBlob(blob: Blob): Blob {
  const forceDownloadTypes = ['application/pdf', 'application/zip']
  if (!blob.type || forceDownloadTypes.includes(blob.type)) {
    return new Blob([blob], { type: 'application/octet-stream' })
  }
  return blob
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const downloadBlob = createDownloadBlob(blob)
  const url = URL.createObjectURL(downloadBlob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  a.setAttribute('data-downloadurl', `${downloadBlob.type}:${filename}:${url}`)
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

type ExportStatus = 'idle' | 'exporting' | 'done' | 'error'

function renderExportStatus(status: ExportStatus) {
  switch (status) {
    case 'exporting':
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-2 py-1 font-medium text-blue-700 text-xs">
          <span className="h-2.5 w-2.5 animate-spin rounded-full border border-blue-500 border-t-transparent" />
          Exporting
        </span>
      )
    case 'done':
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-2 py-1 font-medium text-emerald-700 text-xs">
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-700" />
          Done
        </span>
      )
    case 'error':
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-2 py-1 font-medium text-red-700 text-xs">
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-red-700" />
          Error
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-2 py-1 font-medium text-gray-700 text-xs">
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-gray-400" />
          Ready
        </span>
      )
  }
}

// --- Edge function PDF/ZIP helper ---

async function fetchExportBlob(
  exportType: 'pdf' | 'report-pdf' | 'jpeg',
  yearMonth: string,
  roomNumbers: string[] | null,
  exportUrl = EXPORT_BILL_URL,
  signal?: AbortSignal,
): Promise<Blob> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const token = session?.access_token ?? ''
  const res = await fetch(exportUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ exportType, yearMonth, roomNumbers }),
    signal,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error ?? 'Export failed.')
  }
  return res.blob()
}

const MAX_EXPORT_CONCURRENCY = 10

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<void>,
): Promise<void> {
  const queue = [...items]
  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (queue.length > 0) {
        const item = queue.shift()
        if (item !== undefined) await fn(item)
      }
    },
  )
  await Promise.all(workers)
}

export default function ExportBillPage() {
  const today = new Date()
  const defaultYearMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`

  const navigate = useNavigate()

  const [yearMonth, setYearMonth] = useState(defaultYearMonth)
  const [roomNumber, setRoomNumber] = useState('')
  const [bills, setBills] = useState<BillData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportModalType, setExportModalType] = useState<'pdf' | 'jpeg'>('pdf')
  const [exportModalPhase, setExportModalPhase] = useState<
    'confirm' | 'progress'
  >('confirm')
  const [exportModalBills, setExportModalBills] = useState<BillData[] | null>(
    null,
  )
  const [selectedRooms, setSelectedRooms] = useState<Record<string, boolean>>(
    {},
  )
  const [roomExportStatuses, setRoomExportStatuses] = useState<
    Record<string, ExportStatus>
  >({})
  const exportAbortControllerRef = useRef<AbortController | null>(null)

  const modalBills = exportModalBills ?? bills
  const selectedRoomCount = modalBills.filter(
    (bill) => selectedRooms[bill.roomNumber],
  ).length
  const isAllRoomsSelected =
    modalBills.length > 0 &&
    modalBills.every((bill) => selectedRooms[bill.roomNumber])

  const toggleSelectAll = (checked: boolean) => {
    setSelectedRooms(
      modalBills.reduce<Record<string, boolean>>((acc, bill) => {
        acc[bill.roomNumber] = checked
        return acc
      }, {}),
    )
  }

  const toggleRoomSelection = (roomNumber: string) => {
    setSelectedRooms((prev) => ({
      ...prev,
      [roomNumber]: !prev[roomNumber],
    }))
  }

  // Load room list for dropdown
  const { data: roomOptions } = useQuery({
    queryKey: ['rooms', 'options'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rooms')
        .select('room_number')
        .order('room_number')
      if (error) throw error
      return data as { room_number: string }[]
    },
  })

  const handleGenerate = async () => {
    setError(null)
    setBills([])
    setIsLoading(true)
    try {
      if (roomNumber) {
        const snapshotBill = await fetchSnapshotBillByRoomAndMonth(
          roomNumber,
          yearMonth,
        )
        if (snapshotBill) {
          setBills([snapshotBill])
        } else {
          const bill = await fetchBillByRoomAndMonth(roomNumber, yearMonth)
          setBills([bill])
        }
      } else {
        const snapshotBills = await fetchSnapshotBillsByMonth(yearMonth)
        if (snapshotBills.length > 0) {
          setBills(snapshotBills)
        } else {
          const all = await fetchAllBillsByMonth(yearMonth)
          if (all.length === 0) {
            setError(`No utility records found for ${yearMonth}.`)
          } else {
            setBills(all)
          }
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch bills.')
    } finally {
      setIsLoading(false)
    }
  }

  const exportPdf = async () => {
    if (bills.length === 0) return

    if (bills.length === 1) {
      setIsExporting(true)
      try {
        const blob = await fetchExportBlob('pdf', yearMonth, [
          bills[0].roomNumber,
        ])
        triggerBlobDownload(
          blob,
          `bill-${bills[0].roomNumber}-${yearMonth}.pdf`,
        )
      } catch (e) {
        setError(e instanceof Error ? e.message : 'PDF export failed.')
      } finally {
        setIsExporting(false)
      }
      return
    }

    setError(null)
    setShowExportModal(true)
    setExportModalType('pdf')
    setExportModalPhase('confirm')
    setExportModalBills(bills)
    setSelectedRooms(
      bills.reduce<Record<string, boolean>>((acc, bill) => {
        acc[bill.roomNumber] = true
        return acc
      }, {}),
    )
    setRoomExportStatuses(
      bills.reduce<Record<string, ExportStatus>>((acc, bill) => {
        acc[bill.roomNumber] = 'idle'
        return acc
      }, {}),
    )
  }

  const exportAllPdfsSequentially = async () => {
    const billsToExport = (exportModalBills ?? bills).filter(
      (bill) => selectedRooms[bill.roomNumber],
    )
    if (billsToExport.length === 0) return

    const controller = new AbortController()
    exportAbortControllerRef.current = controller

    setIsExporting(true)
    setExportModalPhase('progress')
    setRoomExportStatuses(
      modalBills.reduce<Record<string, ExportStatus>>((acc, bill) => {
        acc[bill.roomNumber] = 'idle'
        return acc
      }, {}),
    )

    const zip = new JSZip()

    await runWithConcurrency(
      billsToExport,
      MAX_EXPORT_CONCURRENCY,
      async (bill) => {
        if (controller.signal.aborted) return

        setRoomExportStatuses((prev) => ({
          ...prev,
          [bill.roomNumber]: 'exporting',
        }))

        try {
          const blob = await fetchExportBlob(
            'pdf',
            yearMonth,
            [bill.roomNumber],
            EXPORT_BILL_URL,
            controller.signal,
          )
          zip.file(`bill-${bill.roomNumber}-${yearMonth}.pdf`, blob)
          setRoomExportStatuses((prev) => ({
            ...prev,
            [bill.roomNumber]: 'done',
          }))
        } catch (e) {
          if (controller.signal.aborted) {
            setError('Export cancelled.')
            return
          }

          setRoomExportStatuses((prev) => ({
            ...prev,
            [bill.roomNumber]: 'error',
          }))
          setError(
            e instanceof Error
              ? e.message
              : `Failed to export ${bill.roomNumber}.`,
          )
        }
      },
    )

    if (!controller.signal.aborted && billsToExport.length > 0) {
      try {
        const zipBlob = await zip.generateAsync({ type: 'blob' })
        triggerBlobDownload(zipBlob, `bills-${yearMonth}.zip`)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'ZIP generation failed.')
      }
    }

    exportAbortControllerRef.current = null
    setIsExporting(false)
  }

  const exportSelectedJpegs = async () => {
    const billsToExport = (exportModalBills ?? bills).filter(
      (bill) => selectedRooms[bill.roomNumber],
    )
    if (billsToExport.length === 0) return

    const controller = new AbortController()
    exportAbortControllerRef.current = controller

    setIsExporting(true)
    setExportModalPhase('progress')
    setRoomExportStatuses(
      modalBills.reduce<Record<string, ExportStatus>>((acc, bill) => {
        acc[bill.roomNumber] = 'idle'
        return acc
      }, {}),
    )

    const isMultiple = billsToExport.length > 1
    const zip = isMultiple ? new JSZip() : null

    await runWithConcurrency(
      billsToExport,
      MAX_EXPORT_CONCURRENCY,
      async (bill) => {
        if (controller.signal.aborted) return

        setRoomExportStatuses((prev) => ({
          ...prev,
          [bill.roomNumber]: 'exporting',
        }))

        try {
          const blob = await fetchExportBlob(
            'jpeg',
            yearMonth,
            [bill.roomNumber],
            EXPORT_BILL_JPEG_URL,
            controller.signal,
          )
          if (zip) {
            zip.file(`bill-${bill.roomNumber}-${yearMonth}.jpg`, blob)
          } else {
            triggerBlobDownload(
              blob,
              `bill-${bill.roomNumber}-${yearMonth}.jpg`,
            )
          }
          setRoomExportStatuses((prev) => ({
            ...prev,
            [bill.roomNumber]: 'done',
          }))
        } catch (e) {
          if (controller.signal.aborted) {
            setError('Export cancelled.')
            return
          }
          setRoomExportStatuses((prev) => ({
            ...prev,
            [bill.roomNumber]: 'error',
          }))
          setError(
            e instanceof Error
              ? e.message
              : `Failed to export ${bill.roomNumber}.`,
          )
        }
      },
    )

    if (!controller.signal.aborted && zip) {
      try {
        const zipBlob = await zip.generateAsync({ type: 'blob' })
        triggerBlobDownload(zipBlob, `bills-${yearMonth}.zip`)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'ZIP generation failed.')
      }
    }

    exportAbortControllerRef.current = null
    setIsExporting(false)
  }

  const cancelExport = () => {
    exportAbortControllerRef.current?.abort()
    exportAbortControllerRef.current = null
    setIsExporting(false)
    setShowExportModal(false)
    setExportModalPhase('confirm')
    setExportModalBills(null)
    setRoomExportStatuses({})
  }

  const closeExportModal = () => {
    setShowExportModal(false)
    setExportModalPhase('confirm')
    setExportModalBills(null)
    setIsExporting(false)
    setRoomExportStatuses({})
  }

  const isConfirmExportDisabled = !exportModalBills || selectedRoomCount === 0
  const isExportAllInProgress = exportModalPhase === 'progress' && isExporting

  const exportJpeg = async () => {
    if (bills.length === 0) return

    if (bills.length === 1) {
      setIsExporting(true)
      try {
        const blob = await fetchExportBlob(
          'jpeg',
          yearMonth,
          [bills[0].roomNumber],
          EXPORT_BILL_JPEG_URL,
        )
        triggerBlobDownload(
          blob,
          `bill-${bills[0].roomNumber}-${yearMonth}.jpg`,
        )
      } catch (e) {
        setError(e instanceof Error ? e.message : 'JPEG export failed.')
      } finally {
        setIsExporting(false)
      }
      return
    }

    setError(null)
    setShowExportModal(true)
    setExportModalType('jpeg')
    setExportModalPhase('confirm')
    setExportModalBills(bills)
    setSelectedRooms(
      bills.reduce<Record<string, boolean>>((acc, bill) => {
        acc[bill.roomNumber] = true
        return acc
      }, {}),
    )
    setRoomExportStatuses(
      bills.reduce<Record<string, ExportStatus>>((acc, bill) => {
        acc[bill.roomNumber] = 'idle'
        return acc
      }, {}),
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-bold text-2xl text-gray-800">Export Bill</h2>
      </div>

      {/* Filter */}
      <Card className="mb-6 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="yearMonth"
              className="font-medium text-gray-700 text-sm"
            >
              Month
            </label>
            <MonthInput
              id="yearMonth"
              value={yearMonth}
              onValueChange={setYearMonth}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label
              htmlFor="roomSelect"
              className="font-medium text-gray-700 text-sm"
            >
              Room
            </label>
            <select
              id="roomSelect"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">All rooms</option>
              {roomOptions?.map((r) => (
                <option key={r.room_number} value={r.room_number}>
                  {r.room_number}
                </option>
              ))}
            </select>
          </div>

          <Button onClick={handleGenerate} disabled={isLoading || !yearMonth}>
            {isLoading ? 'Loading…' : 'Generate Preview'}
          </Button>

          <Button
            variant="outline"
            disabled={!roomNumber || !yearMonth}
            onClick={() =>
              navigate(`/admin/bill/${roomNumber}?month=${yearMonth}`)
            }
          >
            View Bill Page
          </Button>
        </div>
      </Card>

      {/* Validation error */}
      {error && (
        <p className="mb-4 rounded-md bg-red-50 px-4 py-2 text-red-600 text-sm">
          {error}
        </p>
      )}

      {/* Export actions */}
      {bills.length > 0 && (
        <>
          <div className="mb-4 flex items-center gap-3">
            <span className="text-gray-600 text-sm">
              {bills.length} bill{bills.length > 1 ? 's' : ''} ready
            </span>
            <Button
              onClick={exportPdf}
              disabled={isExporting}
              variant="default"
            >
              {isExporting ? 'Exporting…' : 'Export PDF'}
            </Button>
            <Button
              onClick={exportJpeg}
              disabled={isExporting}
              variant="outline"
            >
              {isExporting ? 'Exporting…' : 'Export JPEG'}
            </Button>
          </div>

          <div className="mb-6 rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            The exported file and the preview may not look exactly the same, but
            the data will be consistent.
          </div>
        </>
      )}

      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black/30 px-4 py-6">
          <div className="w-full max-w-4xl overflow-hidden rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900 text-lg">
                  {exportModalPhase === 'confirm'
                    ? exportModalType === 'jpeg'
                      ? 'Confirm JPEG Export'
                      : 'Confirm Export'
                    : 'Export Progress'}
                </h3>
                <p className="mt-2 text-gray-600 text-sm">
                  {exportModalPhase === 'confirm'
                    ? `Export ${selectedRoomCount} of ${modalBills.length} selected ${
                        exportModalType === 'jpeg' ? 'JPEG' : 'PDF'
                      }${selectedRoomCount > 1 ? 's' : ''}${
                        selectedRoomCount > 1 ? ' as a single ZIP file' : ''
                      }.`
                    : 'Generating the export. Leave this window open until the download starts.'}
                </p>
              </div>
              {!isExportAllInProgress && (
                <button
                  type="button"
                  className="rounded-full border border-gray-200 bg-white px-3 py-2 text-gray-500 text-sm transition hover:bg-gray-50"
                  onClick={closeExportModal}
                >
                  Close
                </button>
              )}
            </div>

            {exportModalPhase === 'confirm' ? (
              <div className="max-h-[calc(100vh-13rem)] space-y-4 overflow-y-auto pr-2">
                <div className="rounded-2xl border border-gray-200 border-dashed bg-gray-50 p-5 text-gray-700 text-sm">
                  This export will generate a separate{' '}
                  {exportModalType === 'jpeg' ? 'JPEG' : 'PDF'} for each
                  selected room. If multiple rooms are selected, the download
                  will be bundled into one ZIP file.
                </div>
                <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-4">
                  <label className="inline-flex items-center gap-2 font-medium text-gray-700 text-sm">
                    <input
                      type="checkbox"
                      checked={isAllRoomsSelected}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Select all
                  </label>
                  <span className="text-gray-500 text-sm">
                    {selectedRoomCount} of {modalBills.length} selected
                  </span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {modalBills.map((bill) => (
                    <div
                      key={bill.roomNumber}
                      className="rounded-3xl border border-gray-200 bg-gray-50 p-4"
                    >
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={!!selectedRooms[bill.roomNumber]}
                          onChange={() => toggleRoomSelection(bill.roomNumber)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">
                            Room {bill.roomNumber}
                          </div>
                          <div className="mt-3">
                            {renderExportStatus(
                              roomExportStatuses[bill.roomNumber] ?? 'idle',
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="max-h-[calc(100vh-13rem)] space-y-4 overflow-y-auto pr-2">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {modalBills.map((bill) => (
                    <div
                      key={bill.roomNumber}
                      className="rounded-3xl border border-gray-200 bg-gray-50 p-4"
                    >
                      <div className="font-semibold text-gray-900 text-sm">
                        Room {bill.roomNumber}
                      </div>
                      <div className="mt-3">
                        {renderExportStatus(
                          roomExportStatuses[bill.roomNumber] ?? 'idle',
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-gray-600 text-sm">
                  {isExportAllInProgress
                    ? 'Export is in progress. Downloads will appear automatically.'
                    : 'Export process completed. Close when you are ready.'}
                </div>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-3">
              <Button
                onClick={
                  exportModalType === 'jpeg'
                    ? exportSelectedJpegs
                    : exportAllPdfsSequentially
                }
                disabled={isExporting || isConfirmExportDisabled}
              >
                {isExporting ? 'Starting export…' : 'Confirm Export'}
              </Button>
              <Button
                variant="outline"
                onClick={
                  isExportAllInProgress ? cancelExport : closeExportModal
                }
              >
                {isExportAllInProgress ? 'Cancel Export' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bill preview */}
      {bills.length > 0 && (
        <div className="space-y-8">
          {bills.map((bill) => (
            <Card key={bill.recordId} className="overflow-hidden p-0">
              <div className="overflow-x-auto">
                <div
                  style={{ width: A4_W, minHeight: A4_H, background: '#fff' }}
                >
                  <BillTemplate {...bill} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
